import React from "react";
import i18n from "../../../../../i18n/i18n";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../../../common/models/httpError";
import {Api} from "../../../../../helpers/api";
import {OrderCargoFileModel, TrailerOrContainerOrderFileModel} from "../../../../models/order";
import PdfLink from "../../../../../common/components/shared/pdfLink/pdfLink";
import ExportOrderCargoFile from "../pdfFiles/exportOrderCargoFile";
import ImportOrderCargoFile from "../pdfFiles/importOrderCargoFile";
import {OrderService} from "../../../../services/orderService";
import ImportTrailerOrContainerOrderFile from "../pdfFiles/importOrderTrailerOrContainerFile";
import {storesContext} from "../../../../../common/stores/stores";
import {IUserStore} from "../../../../../administration/stores/userStore";
import {OrderDetailModel} from "../../../../models/order";
import ExportOrderTrailerOrContainerFile from "../pdfFiles/exportOrderTrailerOrContainerFile";
import NoSeaTrafficOrderTrailerOrContainerFile from "../pdfFiles/noSeaTrafficOrderTrailerOrContainerFile";
import {ReactComponent as PrintIcon} from "../../../../../statics/icons/icon_print.svg";
import {OrderFilesUtils} from "../../../../../helpers/orderFilesUtils";
import NoSeaTrafficOrderCargoFile from "../pdfFiles/noSeaTrafficOrderCargoFile";
import TransportUtils from "../../../../../helpers/transportUtils";

interface PrintFileButtonProps {
    orderId: string | null;
    orderData?: OrderDetailModel | null;
}

interface PrintFileButtonState {
    orderCargoFileData: OrderCargoFileModel | null;
    responseOrderCargoFile: AxiosResponse<HttpErrorModel[] | any> | undefined;
    trailerOrContainerOrderFileData: TrailerOrContainerOrderFileModel | null;
    responseTrailerOrContainerOrderFile: AxiosResponse<HttpErrorModel[] | any> | undefined;
    renderButton: "none" | "cargo" | "trailerOrContainer"
}

export default class PrintFileButton extends React.Component <PrintFileButtonProps, PrintFileButtonState> {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        this.state = {
            orderCargoFileData: null,
            responseOrderCargoFile: undefined,
            trailerOrContainerOrderFileData: null,
            responseTrailerOrContainerOrderFile: undefined,
            renderButton: "none"
        };
    }

    async componentDidMount() {
        if (!this.props.orderData || !this.props.orderId) return null;
        const userStore = this.context.userStore as IUserStore;

        if (userStore.canPrintOrderFile) {
            await this.setState({renderButton: OrderFilesUtils.getOrderPrintButtonType(this.props.orderData.cargoType)});

            switch (this.state.renderButton) {
                case "cargo":
                    await this.getOrderCargoDataForFile();
                    break;
                case "trailerOrContainer":
                    await this.getTrailerOrContainerOrderDataForFile();
                    break;
            }
        }
    }

    public render(): JSX.Element | null {
        const isNull = !this.renderPrintButton();

        if (isNull) {
            return null;
        } else {
            return (
                <div className="print-button">
                    <div className="print-button-wrapper">{this.renderPrintButton()}</div>
                </div>
            );
        }
    }

    public renderPrintButton(): JSX.Element | null {
        switch (this.state.renderButton) {
            case "cargo":
                return this.renderPrintFileButtonForCargo();
            case "trailerOrContainer":
                return this.renderPrintFileButtonForTrailerOrContainer();
            default:
                return <></>;
        }
    }

    private renderPrintFileButtonForCargo(): JSX.Element | null {
        if (this.isExportRelationType) {
            return this.renderPrintFileButtonForExportCargo();
        } else if (this.isImportRelationType) {
            return this.renderPrintFileButtonForImportCargo();
        } else if(this.isNoSeaTrafficRelationType) {
            return this.renderPrintFileButtonForNoSeaTrafficCargo();
        } else {
            return null;
        }
    }

    private renderPrintFileButtonForExportCargo(): JSX.Element {
        const userStore = this.context.userStore as IUserStore;

        return (
            <PdfLink
                document={<ExportOrderCargoFile order={this.state.orderCargoFileData} username={userStore.username}/>}
                fileName={i18n.t("transportation.order.details.files.exportOrderCargoFileName", {number: this.state.orderCargoFileData?.orderId})}
                buttonText={i18n.t("printToFile")}
                buttonVariant="contained"
                responseFile={this.state.responseOrderCargoFile}
                testId="export-order-cargo-btn"
                endIcon={<PrintIcon className="print-icon"/>}
            />
        );
    }

    private renderPrintFileButtonForImportCargo(): JSX.Element {
        const userStore = this.context.userStore as IUserStore;

        return (
            <PdfLink
                document={<ImportOrderCargoFile order={this.state.orderCargoFileData} username={userStore.username}/>}
                fileName={i18n.t("transportation.order.details.files.importOrderCargoFileName", {number: this.state.orderCargoFileData?.orderId})}
                buttonText={i18n.t("printToFile")}
                buttonVariant="contained"
                responseFile={this.state.responseOrderCargoFile}
                testId="import-order-cargo-btn"
                endIcon={<PrintIcon className="print-icon"/>}
            />
        );
    }

    private renderPrintFileButtonForNoSeaTrafficCargo(): JSX.Element {
        const userStore = this.context.userStore as IUserStore;

        return (
            <PdfLink
                document={<NoSeaTrafficOrderCargoFile order={this.state.orderCargoFileData} username={userStore.username}/>}
                fileName={i18n.t("transportation.order.details.files.noSeaTrafficCargoFileName", {number: this.state.orderCargoFileData?.orderId})}
                buttonText={i18n.t("printToFile")}
                buttonVariant="contained"
                responseFile={this.state.responseOrderCargoFile}
                testId="no-sea-traffic-order-cargo-btn"
                endIcon={<PrintIcon className="print-icon"/>}
            />
        );
    }

    private renderPrintFileButtonForTrailerOrContainer(): JSX.Element | null {
        if (this.isExportRelationType) {
            return this.renderPrintFileButtonForExportTrailerOrContainer();
        } else if (this.isImportRelationType) {
            return this.renderPrintFileButtonForImportTrailerOrContainer();
        } else if (this.isNoSeaTrafficRelationType) {
            return this.renderPrintFileButtonForNoSeaTrafficTrailerOrContainer();
        } else {
            return null;
        }
    }

    private renderPrintFileButtonForImportTrailerOrContainer(): JSX.Element {
        const userStore = this.context.userStore as IUserStore;

        return (
            <PdfLink
                document={<ImportTrailerOrContainerOrderFile order={this.state.trailerOrContainerOrderFileData} username={userStore.username}/>}
                fileName={i18n.t("transportation.order.details.files.importTrailerOrContainerOrderFileName", {number: this.state.trailerOrContainerOrderFileData?.orderId})}
                buttonText={i18n.t("printToFile")}
                buttonVariant="contained"
                responseFile={this.state.responseTrailerOrContainerOrderFile}
                testId="import-trailer-or-container-order-btn"
                endIcon={<PrintIcon className="print-icon"/>}
            />
        );
    }

    private renderPrintFileButtonForExportTrailerOrContainer(): JSX.Element {
        const userStore = this.context.userStore as IUserStore;

        return (
            <PdfLink
                document={<ExportOrderTrailerOrContainerFile order={this.state.trailerOrContainerOrderFileData} username={userStore.username}/>}
                fileName={i18n.t("transportation.order.details.files.exportTrailerOrContainerOrderFileName", {number: this.state.trailerOrContainerOrderFileData?.orderId})}
                buttonText={i18n.t("printToFile")}
                buttonVariant="contained"
                responseFile={this.state.responseTrailerOrContainerOrderFile}
                testId="export-trailer-or-container-order-btn"
                endIcon={<PrintIcon className="print-icon"/>}
            />
        );
    }

    private renderPrintFileButtonForNoSeaTrafficTrailerOrContainer(): JSX.Element {
        const userStore = this.context.userStore as IUserStore;

        return (
            <PdfLink
                document={<NoSeaTrafficOrderTrailerOrContainerFile order={this.state.trailerOrContainerOrderFileData} username={userStore.username}/>}
                fileName={i18n.t("transportation.order.details.files.noSeaTrafficTrailerOrContainerFileName", {number: this.state.trailerOrContainerOrderFileData?.orderId})}
                buttonText={i18n.t("printToFile")}
                buttonVariant="contained"
                responseFile={this.state.responseTrailerOrContainerOrderFile}
                testId="no-sea-traffic-trailer-or-container-order-btn"
                endIcon={<PrintIcon className="print-icon"/>}
            />
        );
    }


    async getOrderCargoDataForFile(): Promise<any> {
        const responseOrderCargoFile = await OrderService.getOrderCargoDataForFile(this.props.orderId!);

        this.setState({
            responseOrderCargoFile,
            orderCargoFileData: Api.isSuccess(responseOrderCargoFile) ? responseOrderCargoFile?.data : null
        });
    }

    async getTrailerOrContainerOrderDataForFile(): Promise<any> {
        const responseTrailerOrContainerOrderFile = await OrderService.getTrailerOrContainerOrderDataForFile(this.props.orderId!);

        this.setState({
            responseTrailerOrContainerOrderFile,
            trailerOrContainerOrderFileData: Api.isSuccess(responseTrailerOrContainerOrderFile) ? responseTrailerOrContainerOrderFile?.data : null,
        });
    }

    private get isExportRelationType(): boolean {
        return OrderFilesUtils.isExportRelationType(this.props.orderData?.relationType || undefined);
    }

    private get isImportRelationType(): boolean {
        return OrderFilesUtils.isImportRelationType(this.props.orderData?.relationType || undefined);
    }

    private get isNoSeaTrafficRelationType(): boolean {
        return TransportUtils.isNoSeaTrafficRelationType(this.props.orderData?.relationType || undefined);
    }
}
